import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Textarea } from '../components/ui/Textarea';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { Tooltip } from '../components/ui/Tooltip';
import { Plus, Filter, Download, Eye, Edit, Search, HardHat, FileText, GraduationCap, Stethoscope, Trash2, Calendar, AlertTriangle, Upload, CheckCircle2, X, ExternalLink, Save, AlertCircle, MapPin, Building2, File } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { MultiSelect } from '../components/ui/MultiSelect';
import { mockData } from '../services/mockData';
import { motion, AnimatePresence } from 'framer-motion';
import { DatePicker } from '../components/ui/DatePicker';
import { WorkerDetailHeader } from '../components/workers/WorkerDetailHeader';
import { WorkerAnagraficaSections } from '../components/workers/WorkerAnagraficaSections';
import { WorkerAnagraficaForm } from '../components/workers/WorkerAnagraficaForm';
import { WorkerTabs, WorkerTab } from '../components/workers/WorkerTabs';
import { WorkerTrainingTab } from '../components/workers/WorkerTrainingTab';
import { WorkerHealthTab } from '../components/workers/WorkerHealthTab';
import { WorkerHealthFields } from '../components/workers/WorkerHealthFields';
import { FileSpreadsheet } from 'lucide-react';
import { AssignTrainingModal } from '../components/workers/AssignTrainingModal';
import { TrainingImportModal } from '../components/workers/TrainingImportModal';
import { getWorkerRuolo } from '../utils/workers';
const initialWorkersData = mockData.getWorkers();
const columnsConfig = [{
  key: 'name',
  label: 'Lavoratore'
}, {
  key: 'company',
  label: 'Azienda'
}, {
  key: 'role',
  label: 'Mansione'
}, {
  key: 'trainingStatus',
  label: 'Formazione'
}, {
  key: 'healthStatus',
  label: 'Sorv. sanitaria'
}, {
  key: 'nextExpiry',
  label: 'Prox. scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function WorkersPage() {
  const {
    canCreate,
    canEdit,
    canDelete
  } = useRBAC();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [activeTab, setActiveTab] = useState<WorkerTab>('info');
  const [formTab, setFormTab] = useState<WorkerTab>('info');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAssignTraining, setShowAssignTraining] = useState(false);
  const [showTrainingImport, setShowTrainingImport] = useState(false);
  const [importStep, setImportStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [importData, setImportData] = useState<any[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Form state for new worker
  const [formData, setFormData] = useState<any>({});
  const [nameError, setNameError] = useState('');
  const [companyError, setCompanyError] = useState('');
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialWorkersData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'company', 'fiscalCode']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData({
      ...item,
      ruolo: getWorkerRuolo(item)
    });
    setNameError('');
    setCompanyError('');
    setFormTab('info');
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      nextVisitDate: '',
      notes: ''
    });
    setNameError('');
    setCompanyError('');
    setFormTab('info');
    setIsModalOpen(true);
  };
  const handleViewDetail = (item: any) => {
    setSelectedItem(item);
    setActiveTab('info');
    setIsDetailOpen(true);
  };
  const handleDetailToEdit = () => {
    setIsDetailOpen(false);
    handleEdit(selectedItem);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }, 1500);
  };
  const handleSave = () => {
    const missingName = !formData.name?.trim();
    const missingCompany = !formData.company;
    setNameError(missingName ? 'Inserisci nome e cognome' : '');
    setCompanyError(missingCompany ? "Seleziona l'azienda" : '');
    if (missingName || missingCompany) {
      setFormTab('info');
      return;
    }
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const mockParsedData = [{
        name: 'Mario Rossi',
        company: 'Azienda 1 SRL',
        role: 'Operaio',
        fiscalCode: 'RSSMRA80A01A123B'
      }, {
        name: 'Laura Bianchi',
        company: 'Azienda 1 SRL',
        role: 'Impiegata',
        fiscalCode: 'BNCLRA85B02B456C'
      }, {
        name: 'Giuseppe Verdi',
        company: 'Azienda 2 SRL',
        role: 'Tecnico',
        fiscalCode: 'VRDGPP78C03C789D'
      }];
      setImportData(mockParsedData);
      setImportStep('preview');
    }
  };
  const handleConfirmImport = () => {
    console.log('Importing workers:', importData);
    setImportStep('success');
    setTimeout(() => {
      setShowImportModal(false);
      setImportStep('upload');
      setImportData([]);
    }, 2000);
  };
  return <Layout data-id="element-6609" data-mp="1pghk1k21hfk">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-6610" data-mp="1oozzunalk8a">
        <div data-id="element-6611" data-mp="6ojo5qfonvpn">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6612" data-mp="6n8hniuus9uu">Risorse umane</h1>
          <p className="text-slate-500 mt-1" data-id="element-6613" data-mp="3oupn9ry9qqa7">
            Gestione dipendenti, formazione e sorveglianza sanitaria.
          </p>
        </div>
        <div className="flex flex-wrap gap-3" data-id="element-6614" data-mp="3tr6c6odal8oc">
          <Button variant="flat" color="secondary" startContent={<Upload className="h-4 w-4" data-id="element-6616" data-mp="3jujsi2wpcnnw" />} onClick={() => setShowImportModal(true)} data-id="element-6615" data-mp="3f3l9uwo8rdhh">
            Import massivo
          </Button>
          <Button variant="flat" color="secondary" startContent={<FileSpreadsheet className="h-4 w-4" data-id="element-6618" data-mp="3a5tf7uw7pd9l" />} onClick={() => setShowTrainingImport(true)} data-id="element-6617" data-mp="364zqx0euxwg6">
            Import formazione
          </Button>
          <Button variant="flat" color="secondary" startContent={<GraduationCap className="h-4 w-4" data-id="element-6620" data-mp="3gm3gma4m8k6v" />} onClick={() => setShowAssignTraining(true)} data-id="element-6619" data-mp="314vepmkzfnvr">
            Associa formazione
          </Button>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-6622" data-mp="37ju6hld9esu0" />} data-id="element-6621" data-mp="3bl5ujmghj603">
            Esporta
          </Button>
          <PermissionGate module="workers" action="create" data-id="element-6623" data-mp="32marukcnge1j">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-6625" data-mp="2swr3x0a2xtzt" />} data-id="element-6624" data-mp="2xnr0g4faea1s">
              Nuovo lavoratore
            </Button>
          </PermissionGate>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-6626" data-mp="2ny7lvaylx2cq">
        <div className="p-4 flex flex-col gap-4" data-id="element-6627" data-mp="2jwcj02112gwu">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6628" data-mp="2jxnme0qk9mnf">
            <div className="flex-1" data-id="element-6629" data-mp="2exj2e0qg29sg">
              <Input placeholder="Cerca lavoratore, azienda, CF..." startContent={<Search className="h-4 w-4" data-id="element-6631" data-mp="29yeurej64k48" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-6630" data-mp="2ewgg6642gviv" />
            </div>
            <div className="flex gap-2" data-id="element-6632" data-mp="ude274wstbry">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6634" data-mp="6pcx65ytwdha" />} data-id="element-6633" data-mp="zebgv7ilnnop">
                Filtri{' '}
                {Object.keys(filters).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6635" data-mp="bp91657j04wu">
                    {Object.keys(filters).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="workers" lockedColumns={['name']} data-id="element-6636" data-mp="kh15dy5yihsz" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-6637" data-mp="v213rmlztvyn">
              <Select label="Stato formazione" value={filters.trainingStatus || ''} onChange={e => setFilter('trainingStatus', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'valid',
            label: 'In regola'
          }, {
            value: 'expiring',
            label: 'In scadenza'
          }, {
            value: 'expired',
            label: 'Scaduto'
          }]} data-id="element-6638" data-mp="143h6d86kgtsb" />
              <Select label="Sorveglianza" value={filters.healthStatus || ''} onChange={e => setFilter('healthStatus', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'fit',
            label: 'Idoneo'
          }, {
            value: 'unfit',
            label: 'Non idoneo'
          }]} data-id="element-6639" data-mp="3trrltlqfp19z" />
              <div className="col-span-full flex justify-end" data-id="element-6640" data-mp="2yixnmsz3fb3q">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-6642" data-mp="2omuolvzjfsl0" />} data-id="element-6641" data-mp="2yhmkmvdh27do">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6643" data-mp="2flwk7cl6tj1w">
        <div className="overflow-x-auto" data-id="element-6644" data-mp="25x7p3vjsjvad">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6645" data-mp="25w6ettalnctp">
            <thead data-id="element-6646" data-mp="29yt87ymx55t0">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6647" data-mp="29zce2gzao3qo">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-6648" data-mp="2omxj4gfqcd18">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6649" data-mp="2jwh5av7zypec">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-6650" data-mp="37ltc5ofuqldh">
                  {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-6651" data-mp="10xjaif1vxzql">
                      {item.name}
                    </td>}
                  {visibleColumns.includes('company') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6652" data-mp="19p2zwakmux8w">
                      {item.company}
                    </td>}
                  {visibleColumns.includes('role') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6653" data-mp="hzqg615buy4t">
                      {item.role}
                    </td>}
                  {visibleColumns.includes('trainingStatus') && <td className="px-4 py-4" data-id="element-6654" data-mp="r16gfg2fhjvb">
                      <Badge variant={item.trainingStatus === 'valid' || item.trainingStatus === 'In regola' ? 'success' : item.trainingStatus === 'expiring' || item.trainingStatus === 'In scadenza' ? 'warning' : 'danger'} data-id="element-6655" data-mp="m0abr0a95tou">
                        {item.trainingStatus === 'valid' || item.trainingStatus === 'In regola' ? 'In regola' : item.trainingStatus === 'expiring' || item.trainingStatus === 'In scadenza' ? 'In scadenza' : 'Scaduto'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('healthStatus') && <td className="px-4 py-4" data-id="element-6656" data-mp="3qcughzwggpar">
                      <Badge variant={item.healthStatus === 'fit' || item.healthStatus === 'Idoneo' ? 'success' : item.healthStatus === 'Parzialmente idoneo' ? 'warning' : 'danger'} data-id="element-6657" data-mp="3qfqfzsnybm1b">
                        {item.healthStatus === 'fit' || item.healthStatus === 'Idoneo' ? 'Idoneo' : item.healthStatus === 'Parzialmente idoneo' ? 'Parzialmente idoneo' : 'Non idoneo'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('nextExpiry') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-6658" data-mp="1xntqdcvr29li">
                      {item.nextExpiry}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-6659" data-mp="2q7io317euj6f">
                      <div className="flex items-center gap-1" data-id="element-6660" data-mp="2ldxudlcuobd1">
                        <Tooltip content="Visualizza lavoratore" data-id="element-6661" data-mp="jhp2nr8kx4d0">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleViewDetail(item)} data-id="element-6662" data-mp="jjjnl5juowh3">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-6663" data-mp="epovpeu3db6x" />
                          </Button>
                        </Tooltip>
                        <PermissionGate module="workers" action="edit" data-id="element-6664" data-mp="3w0yd2unli232">
                          <Tooltip content="Modifica lavoratore" data-id="element-6665" data-mp="4tc18eqq2tb4">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-6666" data-mp="3myp2k4sbu8po">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-6667" data-mp="1l4pr9jf7frcz" />
                            </Button>
                          </Tooltip>
                        </PermissionGate>
                        <PermissionGate module="workers" action="delete" data-id="element-6668" data-mp="20a29un1auokm">
                          <Tooltip content="Elimina lavoratore" data-id="element-6669" data-mp="1vc0rk4bp6ta7">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-6670" data-mp="291vy6vn2l3kr">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-6671" data-mp="2j2emc2lo368h" />
                            </Button>
                          </Tooltip>
                        </PermissionGate>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6672" data-mp="xmflrpiguxrz">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6673" data-mp="xp1rrkppl583" />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica lavoratore' : 'Nuovo lavoratore'} size="2xl" data-id="element-6674" data-mp="1n327lx7qvlaa">
        <AnimatePresence mode="wait" data-id="element-6675" data-mp="1vzt8qo15lqbq">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-6676" data-mp="z8rc0tf2f3gx">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-6677" data-mp="1d4urfgla579c">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-6678" data-mp="2ozq25lh664pu" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-6679" data-mp="2kzftg11zt07a">
                Lavoratore salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-6680" data-mp="2b0qby9e0dhrk">
              <div className="space-y-6" data-id="element-6681" data-mp="2201zhj49jpgs">
                <WorkerTabs active={formTab} onChange={setFormTab} data-id="element-6682" data-mp="2213j4bzchka4" />

                <div className={formTab === 'info' ? '' : 'hidden'} data-id="element-6683" data-mp="4a0r7jjl732f">
                  <WorkerAnagraficaForm value={formData} nameError={nameError} showCompany companyError={companyError} onChange={changes => {
                if (changes.name !== undefined) setNameError('');
                if (changes.company !== undefined) setCompanyError('');
                setFormData((prev: any) => ({
                  ...prev,
                  ...changes
                }));
              }} data-id="element-6684" data-mp="47y00w6p73w4" />
                </div>

                <div className={formTab === 'training' ? '' : 'hidden'} data-id="element-6685" data-mp="1k9wh3cl5jipb">
                  <WorkerTrainingTab key={selectedItem ? `edit-${selectedItem.id}` : 'new'} workerId={selectedItem?.id} workerName={formData.name} data-id="element-6686" data-mp="1k70kpsoyd22z" />
                </div>

                <div className={formTab === 'health' ? '' : 'hidden'} data-id="element-6687" data-mp="16an93073nuwf">
                  <WorkerHealthFields nextVisitDate={formData.nextVisitDate} notes={formData.notes} onChange={changes => setFormData((prev: any) => ({
                ...prev,
                ...changes
              }))} data-id="element-6688" data-mp="35n9bnuh41jpp" />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-6689" data-mp="3d5774v2giu9x">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-6690" data-mp="3hwp6xfsquotb">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-6692" data-mp="2u85xuy1ve806" />} data-id="element-6691" data-mp="2p9up6qnba3jj">
                    Salva lavoratore
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title="Scheda lavoratore" size="2xl" data-id="element-6693" data-mp="egrvl3iujate">
        {selectedItem && <div className="flex flex-col h-[70vh]" data-id="element-6694" data-mp="2fig70e0zost5">
            {/* Header */}
            <WorkerDetailHeader worker={selectedItem} data-id="element-6695" data-mp="2fkkl1oslr875" />

            {/* Tabs */}
            <WorkerTabs active={activeTab} onChange={setActiveTab} data-id="element-6696" data-mp="2aivbyeoxx4lp" />

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pt-1 px-1" data-id="element-6697" data-mp="2oid1iwa28lw4">
              <div className={activeTab === 'info' ? '' : 'hidden'} data-id="element-6698" data-mp="2jlcuai1h5d3f">
                <WorkerAnagraficaSections worker={selectedItem} data-id="element-6699" data-mp="1vtz4aiqsptlm" />
              </div>

              <div className={activeTab === 'training' ? '' : 'hidden'} data-id="element-6700" data-mp="1rt5efjtsm4q3">
                <WorkerTrainingTab key={`detail-${selectedItem.id}`} workerId={selectedItem.id} workerName={selectedItem.name} data-id="element-6701" data-mp="1rqsq8oxfyy4l" />
              </div>

              <div className={activeTab === 'health' ? '' : 'hidden'} data-id="element-6702" data-mp="1d3zh61eimoku">
                <WorkerHealthTab key={`health-${selectedItem.id}`} data-id="element-6703" data-mp="1d1wi6rvdxl3z" />
              </div>

            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-auto" data-id="element-6704" data-mp="3jrq402y4p1xx">
              <Button variant="flat" color="default" onClick={() => setIsDetailOpen(false)} data-id="element-6705" data-mp="3oiq1paf2no1h">
                Chiudi
              </Button>
              <Button onClick={handleDetailToEdit} startContent={<Edit className="h-4 w-4" data-id="element-6707" data-mp="7auwfuk1akpz" />} data-id="element-6706" data-mp="3fq4umrksz5sh">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      <AssignTrainingModal isOpen={showAssignTraining} onClose={() => setShowAssignTraining(false)} workers={initialWorkersData} showCompanyFilter data-id="element-6708" data-mp="bbwnvp7plshe" />
      <TrainingImportModal isOpen={showTrainingImport} onClose={() => setShowTrainingImport(false)} workers={initialWorkersData} data-id="element-6709" data-mp="2aglo4r1swo9" />

      {/* Import Modal */}
      <Modal isOpen={showImportModal} onClose={() => {
      setShowImportModal(false);
      setImportStep('upload');
      setImportData([]);
    }} title="Import massivo lavoratori" size="lg" data-id="element-6710" data-mp="2n30j03r7ft7q">
        <AnimatePresence mode="wait" data-id="element-6711" data-mp="2xj2q8nxmas6o">
          {importStep === 'upload' && <motion.div key="upload" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-6712" data-mp="2tjk79xpcwr6h">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center" data-id="element-6713" data-mp="3h6sama385q5v">
                <Upload className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-6714" data-mp="3cbwh15icauqn" />
                <p className="text-slate-600 mb-2" data-id="element-6715" data-mp="37a75lor7gf21">
                  Trascina qui il file CSV o clicca per selezionarlo
                </p>
                <p className="text-sm text-slate-400 mb-4" data-id="element-6716" data-mp="1vvjwf9mcqgmn">
                  Formato supportato: CSV con colonne Nome, Azienda, Mansione,
                  CF
                </p>
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileUpload} data-id="element-6717" data-mp="1lpioqydxzuoo" />
                <Button onClick={() => fileInputRef.current?.click()} startContent={<Upload className="h-4 w-4" data-id="element-6719" data-mp="24id0zkk6l8p7" />} data-id="element-6718" data-mp="20g9e7zs2ahom">
                  Seleziona file
                </Button>
              </div>
            </motion.div>}

          {importStep === 'preview' && <motion.div key="preview" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-6720" data-mp="2eg0vc9dvke6u">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2" data-id="element-6721" data-mp="p25l0vtasxkm">
                <CheckCircle2 className="h-5 w-5 text-green-600" data-id="element-6722" data-mp="h1g5x0nu6vvl" />
                <span className="text-green-700 font-medium" data-id="element-6723" data-mp="c23es3pxn2n7">
                  {importData.length} lavoratori trovati nel file
                </span>
              </div>
              <div className="border border-slate-100 rounded-xl overflow-hidden max-h-64 overflow-y-auto" data-id="element-6724" data-mp="qwhu9uaaftx4">
                <table className="w-full text-sm text-left" data-id="element-6725" data-mp="zziujech2qsv">
                  <thead className="bg-slate-50 text-slate-500 font-semibold sticky top-0" data-id="element-6726" data-mp="zxppg3hicemz">
                    <tr data-id="element-6727" data-mp="uzwj9ag7fkmp">
                      <th className="p-3" data-id="element-6728" data-mp="uylg9cul2gwn">Nome</th>
                      <th className="p-3" data-id="element-6729" data-mp="19oam3ldmf939">Azienda</th>
                      <th className="p-3" data-id="element-6730" data-mp="19r6mdgcxy9uv">Mansione</th>
                      <th className="p-3" data-id="element-6731" data-mp="14rk3orecg1eo">CF</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-6732" data-mp="1iqtne11rddne">
                    {importData.map((row, i) => <tr key={i} data-id="element-6733" data-mp="1eq9q8xh6qa19" data-mpi={i}>
                        <td className="p-3 font-medium" data-id="element-6734" data-mp="1xcpih8kixdet">{row.name}</td>
                        <td className="p-3" data-id="element-6735" data-mp="1xamlg4kyek0l">{row.company}</td>
                        <td className="p-3" data-id="element-6736" data-mp="22ar523h5ruv1">{row.role}</td>
                        <td className="p-3 font-mono text-xs" data-id="element-6737" data-mp="1oc14uez1lf2g">
                          {row.fiscalCode}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3" data-id="element-6738" data-mp="1elfzm8ov5cak">
                <Button variant="flat" color="default" onClick={() => setImportStep('upload')} data-id="element-6739" data-mp="1jco914q70xnq">
                  Indietro
                </Button>
                <Button onClick={handleConfirmImport} startContent={<CheckCircle2 className="h-4 w-4" data-id="element-6741" data-mp="vmtwko9sdb40" />} data-id="element-6740" data-mp="19md53i6ieogu">
                  Conferma import
                </Button>
              </div>
            </motion.div>}

          {importStep === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-12" data-id="element-6742" data-mp="1g36ikxhvtpk8">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-6743" data-mp="126szxl1m3i46">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-6744" data-mp="efnorj6m9e07" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-6745" data-mp="9fhqvl9r6v9o">
                Import completato con successo
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-6746" data-mp="2l2l8ctqhbjzw">
                {importData.length} lavoratori importati
              </p>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-6747" data-mp="2z35pllgqx7ra">
        <AnimatePresence mode="wait" data-id="element-6748" data-mp="22eg6xsxaob49">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-6749" data-mp="27d1b23fo3exz">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-6750" data-mp="1nmo58jqfavl0">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-6751" data-mp="en2567620nsg" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-6752" data-mp="jetzmssbzdnv">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-6753" data-mp="5ksyxthtjgkv">
              <div className="space-y-6" data-id="element-6754" data-mp="3rwgcnu3k9sz7">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-6755" data-mp="o2jn8du8p0i">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-6756" data-mp="3d6vjwh8epfap" />
                  <div data-id="element-6757" data-mp="395tipuuxit63">
                    <p className="text-sm text-red-700 font-medium" data-id="element-6758" data-mp="396ld52csikus">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-6759" data-mp="3n38ah68nnd18">
                      Il lavoratore <strong data-id="element-6760" data-mp="3i56pugvkz5nn">{itemToDelete?.name}</strong> verrà
                      eliminato definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-6761" data-mp="3s3wc0luifc9p">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-6762" data-mp="9qeux0fw5z2k">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-6764" data-mp="erm493xon6bc" />} data-id="element-6763" data-mp="jpnq1wmhtjqi">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}